import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useSymbols } from '../api';
import { useHftStore } from '../store';
import CandleChart from './CandleChart';
import TickTable from './TickTable';

export default function SegmentExplorer() {
  const { current, push } = useHftStore();
  const { date, segment } = current.nav;
  const [search, setSearch] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [tab, setTab] = useState<'candles' | 'ticks'>('candles');
  const [tf, setTf] = useState('1m');

  const { data: symbolsData, isLoading } = useSymbols(segment, date, search);

  const symbols = symbolsData?.symbols || [];

  return (
    <Box display="flex" gap={2} height="calc(100vh - 280px)" minHeight={500}>
      {/* Left: Symbol list */}
      <Paper
        elevation={0}
        sx={{
          width: 260,
          flexShrink: 0,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: (t) =>
            t.palette.mode === 'dark' ? 'rgba(30,41,59,0.5)' : 'rgba(241,245,249,0.8)',
        }}
      >
        <Box p={1}>
          <TextField
            size="small"
            placeholder="Search symbols..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
          />
        </Box>
        <Divider />

        {isLoading ? (
          <LinearProgress />
        ) : (
          <List
            dense
            sx={{
              flex: 1,
              overflow: 'auto',
              '& .MuiListItemButton-root': { py: 0.3 },
            }}
          >
            {symbols.map((sym) => (
              <ListItemButton
                key={sym}
                selected={sym === selectedSymbol}
                onClick={() => {
                  setSelectedSymbol(sym);
                  push('segment', { symbol: sym });
                }}
              >
                <ListItemText
                  primary={sym}
                  primaryTypographyProps={{ fontSize: 12, fontFamily: 'monospace' }}
                />
              </ListItemButton>
            ))}
            {symbols.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                No symbols found
              </Typography>
            )}
          </List>
        )}

        <Divider />
        <Typography variant="caption" color="text.secondary" textAlign="center" py={0.5}>
          {symbols.length} symbols
        </Typography>
      </Paper>

      {/* Right: Chart + Ticks */}
      <Box flex={1} display="flex" flexDirection="column" minWidth={0}>
        {selectedSymbol ? (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, flex: 1 }}>
                <Tab label="Candles" value="candles" sx={{ minHeight: 36, py: 0 }} />
                <Tab label="Ticks" value="ticks" sx={{ minHeight: 36, py: 0 }} />
              </Tabs>
              {date && (
                <Button
                  component={RouterLink}
                  to={`/dashboard?view=ticklake&date=${encodeURIComponent(date)}&symbol=${encodeURIComponent(selectedSymbol)}&tf=${encodeURIComponent(tf)}&rth=1`}
                  size="small"
                  variant="outlined"
                  startIcon={<ExternalLink size={14} />}
                  sx={{ textTransform: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Open in Tick Studio
                </Button>
              )}
            </Box>

            {tab === 'candles' && segment && date && (
              <CandleChart segment={segment} date={date} symbol={selectedSymbol} tf={tf} onTfChange={setTf} />
            )}
            {tab === 'ticks' && segment && date && (
              <TickTable segment={segment} date={date} symbol={selectedSymbol} />
            )}
          </>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Typography color="text.secondary">Select a symbol from the list</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
